import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Users, Shield } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Header */}
      <nav className="bg-white/90 dark:bg-slate-950/80 backdrop-blur-sm border-b border-rose-100 dark:border-slate-800 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 dark:text-slate-200 hover:text-rose-600"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <FileText className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-gray-900 dark:text-white">
                Terms of Service
              </span>
            </div>

            <div className="w-20"></div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600">
              Legal agreement for using Unity Love platform
            </p>
            <div className="text-sm text-gray-500 mt-4">
              Last updated: January 2025
            </div>
          </div>

          <div className="space-y-8">
            {/* Acceptance */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Acceptance of Terms
                </h2>
              </div>
              <div className="bg-rose-50 border border-rose-200 p-4 rounded-lg">
                <p className="text-gray-700 leading-relaxed">
                  By accessing or using Unity Love ("Service," "Platform," "we," "us," or "our"), you agree to be legally bound by these Terms of Service. Our platform is designed for African American and African couples seeking meaningful connections with single women through consensual, ethical non-monogamous relationships.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>If you do not agree to these terms, you must not use our Service.</strong>
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Users className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  User Eligibility
                </h2>
              </div>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Age Requirement: 18+ Only</h3>
                  <p className="text-gray-700">
                    You must be at least 18 years old to use Unity Love. By registering, you represent and warrant that you meet this age requirement and have the legal capacity to enter into this agreement.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">User Types</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• <strong>Couples:</strong> Two consenting adults in a committed relationship seeking connections</li>
                    <li>• <strong>Single Women:</strong> Individuals interested in connecting with couples</li>
                    <li>• All users must be authentic, honest, and respectful</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Account Responsibilities */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Account Security & Responsibilities
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Your Responsibilities</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Provide accurate, truthful information</li>
                    <li>• Maintain one active account only</li>
                    <li>• Keep login credentials secure</li>
                    <li>• Enable two-factor authentication when available</li>
                    <li>• Report unauthorized account access immediately</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Account Security</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Use strong, unique passwords</li>
                    <li>• Don't share account access with others</li>
                    <li>• Log out from shared devices</li>
                    <li>• Monitor account activity regularly</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Content and Conduct */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Content and Conduct Policy
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">✅ Acceptable Use</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Genuine profile photos and information</li>
                    <li>• Respectful, honest communication</li>
                    <li>• Consensual interactions and conversations</li>
                    <li>• Cultural sensitivity and inclusivity</li>
                    <li>• Appropriate content for adult dating platform</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">❌ Prohibited Activities</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Fake profiles, impersonation, or catfishing</li>
                    <li>• Harassment, bullying, or discriminatory behavior</li>
                    <li>• Explicit sexual content or solicitation</li>
                    <li>• Commercial advertising or spam</li>
                    <li>• Sharing non-consensual intimate images</li>
                    <li>• Hate speech or threats of violence</li>
                    <li>• Scamming or fraudulent activities</li>
                    <li>• Underage users or content</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Consent and Communication */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Consent and Ethical Communication
              </h2>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Core Principles</h3>
                <div className="space-y-2 text-gray-700">
                  <p>• <strong>Explicit Consent:</strong> Always obtain clear consent before sharing personal information, photos, or meeting in person</p>
                  <p>• <strong>Honest Communication:</strong> Be transparent about your relationship status, intentions, and boundaries</p>
                  <p>• <strong>Respect Boundaries:</strong> Accept "no" gracefully and respect all stated boundaries</p>
                  <p>• <strong>Privacy:</strong> Do not share others' personal information, messages, or photos without permission</p>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Intellectual Property
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Your Content</h3>
                  <p className="text-gray-700">
                    You retain ownership of content you post (photos, messages, profile information). By using our Service, you grant Unity Love a license to use this content to operate and improve the platform.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Our Platform</h3>
                  <p className="text-gray-700">
                    Unity Love platform, design, features, and trademarks are owned by us and protected by intellectual property laws.
                  </p>
                </div>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Account Termination
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">By You</h3>
                  <p className="text-gray-700">
                    You may delete your account at any time through account settings. Upon deletion, your profile and data will be removed according to our Privacy Policy.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">By Us</h3>
                  <p className="text-gray-700">
                    We may suspend or terminate accounts that violate these terms, engage in harmful behavior, or compromise platform safety. Appeals can be submitted to support.
                  </p>
                </div>
              </div>
            </section>

            {/* Disclaimer */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Disclaimers and Limitations
              </h2>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>Platform Use:</strong> Unity Love is provided "as is" without warranties. We do not guarantee matches, relationships, or specific outcomes.
                  </p>
                  <p>
                    <strong>User Interactions:</strong> You are solely responsible for your interactions with other users. We cannot control user behavior or guarantee the accuracy of profile information.
                  </p>
                  <p>
                    <strong>Offline Meetings:</strong> Any in-person meetings are at your own risk. Always meet in public places and inform others of your plans.
                  </p>
                  <p>
                    <strong>Limitation of Liability:</strong> Unity Love is not liable for damages arising from platform use, user interactions, or offline meetings.
                  </p>
                </div>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Changes to Terms
              </h2>
              <p className="text-gray-700">
                We may update these Terms of Service periodically. Material changes will be communicated via email or platform notification. Continued use after changes constitutes acceptance of updated terms.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Contact Information
              </h2>
              <div className="bg-rose-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-3">
                  Questions about these Terms of Service?
                </p>
                <div className="space-y-2 text-gray-700">
                  <div>📧 Email: legal@joinunitylove.com</div>
                  <div className="text-sm text-gray-500 mt-2">
                    Email us and we'll respond within 1–2 business days.
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;